import { formatTimestamp, type ConfirmedPerson } from '@sl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ConfirmPeopleDialog } from '../components/ConfirmPeopleDialog.js';
import { MomPanel } from '../components/MomPanel.js';
import { StatusChip } from '../components/StatusChip.js';
import { TranscriptPane } from '../components/TranscriptPane.js';
import { meetingsApi, type ChunkDiagnosticRow, type ClientLogRow } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { countLinesToCheck, needsCheck } from '../lib/confidence.js';
import {
  citeTarget,
  defaultTab,
  showBackToMinutes,
  TWO_PANE_QUERY,
  visibleTabs,
  type MeetingTab,
} from '../lib/meetingTabs.js';

const PROCESSING = new Set(['RECORDING', 'UPLOADED', 'TRANSCRIBING', 'DRAFTING']);

/** Tracks the two-pane breakpoint; the query string lives beside the CSS. */
function useTwoPane(): boolean {
  const [twoPane, setTwoPane] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(TWO_PANE_QUERY).matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(TWO_PANE_QUERY);
    const onChange = (e: MediaQueryListEvent): void => setTwoPane(e.matches);
    mql.addEventListener('change', onChange);
    setTwoPane(mql.matches);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return twoPane;
}

export function MeetingPage(): JSX.Element {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const { canWrite, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const audioRef = useRef<HTMLAudioElement>(null);
  const refreshingAudio = useRef(false);
  /** A dead URL must not retry forever: that is what made the player flicker. */
  const audioRefreshes = useRef(0);
  const twoPane = useTwoPane();

  const [activeSegment, setActiveSegment] = useState<string | null>(null);
  const [peopleDialog, setPeopleDialog] = useState(false);
  const [showOnlyCheck, setShowOnlyCheck] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<number | undefined>(undefined);
  const [tab, setTab] = useState<MeetingTab | null>(null);
  const [editingRef, setEditingRef] = useState(false);
  const [refDraft, setRefDraft] = useState('');
  /** True while the reviewer is on the transcript because a citation sent them
   * there — the only case that earns a "Back to minutes" button. */
  const [cameFromCitation, setCameFromCitation] = useState(false);

  const meetingQuery = useQuery({
    queryKey: ['meeting', id],
    queryFn: () => meetingsApi.get(id),
    enabled: Boolean(id),
  });

  const meeting = meetingQuery.data?.meeting;
  const processing = meeting ? PROCESSING.has(meeting.status) : false;

  const transcriptQuery = useQuery({
    queryKey: ['transcript', id, selectedVersion],
    queryFn: () => meetingsApi.transcript(id, selectedVersion),
    enabled: Boolean(id),
    // Only the latest version needs to poll while the meeting is processing.
    refetchInterval: processing && selectedVersion === undefined ? 8000 : false,
  });

  const versionsQuery = useQuery({
    queryKey: ['transcript-versions', id],
    queryFn: () => meetingsApi.transcriptVersions(id),
    enabled: Boolean(id),
  });
  const versions = versionsQuery.data?.items ?? [];

  const segments = transcriptQuery.data?.segments ?? [];
  const hasAudio = Boolean(transcriptQuery.data?.audioUrl);

  // Which tab opens first depends on whether there are minutes to read, so the
  // choice waits for the MOM panel to report in. Until then nothing is picked,
  // and the fallback is the transcript.
  const [hasMom, setHasMom] = useState<boolean | null>(null);
  useEffect(() => {
    if (tab === null && hasMom !== null) setTab(defaultTab({ hasMom }));
  }, [tab, hasMom]);
  const activeTab: MeetingTab = tab ?? 'transcript';
  const tabs = visibleTabs(isAdmin);

  // Browser-side upload diagnostics — an operator tool, so ADMIN only.
  const clientLogQuery = useQuery({
    queryKey: ['client-log', id],
    queryFn: () => meetingsApi.clientLog(id),
    enabled: Boolean(id) && isAdmin,
  });

  // The audio src must NOT follow the query result: every refetch (the 8 s
  // processing poll, a segment edit, speaker naming) mints a new presigned URL,
  // and a changed src makes the browser reload the element and stop playback.
  // Pin the first URL we get; refreshAudio() replaces it only when it expires.
  const [audioSrc, setAudioSrc] = useState<string | null>(null);
  const latestAudioUrl = transcriptQuery.data?.audioUrl ?? null;
  useEffect(() => {
    if (latestAudioUrl && !audioSrc) setAudioSrc(latestAudioUrl);
  }, [latestAudioUrl, audioSrc]);

  // "Speaker 10" must not sort before "Speaker 2".
  const speakers = useMemo(
    () =>
      [...new Set(segments.map((s) => s.speaker))].sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }),
      ),
    [segments],
  );

  const lowConfidenceCount = useMemo(() => countLinesToCheck(segments), [segments]);
  // Only the rendered list is filtered; `speakers` and every other derived
  // value stay on the full transcript.
  const visibleSegments = showOnlyCheck ? segments.filter((s) => needsCheck(s.confidence)) : segments;

  const confirmPeople = useMutation({
    mutationFn: (input: {
      people: ConfirmedPerson[];
      realClientName: string | null;
      preparedByOverride: string | null;
    }) => meetingsApi.confirmPeople(id, input),
    onSuccess: (data) => {
      setPeopleDialog(false);
      setNotice(t('people.saved', { n: data.relabelledSegments }) as string);
      void queryClient.invalidateQueries({ queryKey: ['meeting', id] });
      void queryClient.invalidateQueries({ queryKey: ['transcript', id] });
    },
  });

  const saveExternalRef = useMutation({
    mutationFn: (value: string) => meetingsApi.update(id, { externalRef: value.trim() || null }),
    onSuccess: () => {
      setEditingRef(false);
      setNotice(t('meeting.externalRefSaved') as string);
      void queryClient.invalidateQueries({ queryKey: ['meeting', id] });
    },
  });

  const retranscribe = useMutation({
    mutationFn: () => meetingsApi.retranscribe(id),
    onSuccess: () => {
      setNotice(t('meeting.retranscribing'));
      void queryClient.invalidateQueries({ queryKey: ['meeting', id] });
      void queryClient.invalidateQueries({ queryKey: ['transcript', id] });
    },
  });

  /**
   * A citation chip in the minutes names a transcript segment by its idx.
   * Jumping to it scrolls the line into view, highlights it, and — when there
   * is audio — plays from that moment, which is the point of the citations.
   *
   * On a wide screen the transcript is already beside the editor and the jump
   * happens in place. On a narrow one it has to switch tabs, so the reviewer
   * is given a way back to where they were reading.
   */
  const jumpToSegmentIdx = (idx: number): void => {
    const segment = segments.find((s) => s.idx === idx);
    if (!segment) return;
    // A filtered list can be hiding the cited line; show everything first.
    setShowOnlyCheck(false);
    if (citeTarget({ twoPane, activeTab }) === 'transcript-tab') {
      setTab('transcript');
      setCameFromCitation(true);
    }
    seek(segment.startSec, segment.id);
    // After the state update has rendered the highlight and the tab switch.
    requestAnimationFrame(() => {
      document
        .getElementById(`segment-${segment.id}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  /** Takes a time rather than a segment so a single word can seek to itself. */
  const seek = (time: number, segmentId: string): void => {
    setActiveSegment(segmentId);
    const audio = audioRef.current;
    if (!audio) return;
    // Setting currentTime before metadata has loaded is silently dropped.
    if (audio.readyState < 1) {
      const onLoaded = (): void => {
        audio.currentTime = time;
        void audio.play().catch(() => undefined);
      };
      audio.addEventListener('loadedmetadata', onLoaded, { once: true });
      audio.load();
      return;
    }
    audio.currentTime = time;
    void audio.play().catch(() => undefined);
  };

  /**
   * Presigned audio URLs last five minutes. When one expires mid-review the
   * element errors; refetch the transcript for a fresh URL and carry on from
   * the same position.
   */
  const refreshAudio = async (): Promise<void> => {
    const audio = audioRef.current;
    const resumeAt = audio?.currentTime ?? 0;
    const wasPlaying = audio ? !audio.paused : false;
    if (refreshingAudio.current) return;
    if (audioRefreshes.current >= 3) return;
    audioRefreshes.current += 1;
    refreshingAudio.current = true;
    try {
      const fresh = await queryClient.fetchQuery({
        queryKey: ['transcript', id],
        queryFn: () => meetingsApi.transcript(id),
      });
      if (!fresh.audioUrl || !audioRef.current) return;
      const el = audioRef.current;
      setAudioSrc(fresh.audioUrl);
      el.src = fresh.audioUrl;
      const onLoaded = (): void => {
        el.currentTime = resumeAt;
        if (wasPlaying) void el.play().catch(() => undefined);
      };
      el.addEventListener('loadedmetadata', onLoaded, { once: true });
      el.load();
    } finally {
      refreshingAudio.current = false;
    }
  };

  if (meetingQuery.isLoading) return <p className="muted">{t('common.loading')}</p>;
  if (!meeting) return <div className="banner error">{t('common.error')}</div>;

  const transcriptControls = (
    <>
      <div className="spread" style={{ marginBottom: 10 }}>
        <h2 style={{ marginBottom: 0 }}>{t('meeting.transcript')}</h2>
        <div className="row">
          {canWrite && segments.length > 0 && (
            <button type="button" className="secondary" onClick={() => setPeopleDialog(true)}>
              {t('people.open')}
            </button>
          )}
          {canWrite && (
            <button
              type="button"
              className="secondary"
              disabled={retranscribe.isPending || !hasAudio}
              title={!hasAudio ? (t('meeting.retranscribeDisabledTitle') as string) : undefined}
              onClick={() => retranscribe.mutate()}
            >
              {t('meeting.retranscribe')}
            </button>
          )}
        </div>
      </div>

      {transcriptQuery.data?.transcript && (
        <p className="muted">
          {t('meeting.provider')}: {transcriptQuery.data.transcript.provider} /{' '}
          {transcriptQuery.data.transcript.model} · v{transcriptQuery.data.transcript.version}
          {transcriptQuery.data.transcript.costUsd !== null
            ? ` · ${t('meeting.cost')}: $${transcriptQuery.data.transcript.costUsd.toFixed(3)}`
            : ''}
        </p>
      )}

      {versions.length > 1 && (
        <div className="field" style={{ maxWidth: 260 }}>
          <label htmlFor="transcriptVersion">{t('meeting.version')}</label>
          <select
            id="transcriptVersion"
            value={selectedVersion ?? versions[0]!.version}
            onChange={(e) => {
              const v = Number(e.target.value);
              setSelectedVersion(v === versions[0]!.version ? undefined : v);
            }}
          >
            {versions.map((v) => (
              <option key={v.version} value={v.version}>
                {t('meeting.versionOption', { version: v.version, provider: v.provider })}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Only meaningful when the engine reported word confidence (imported and
          Zoom-engine transcripts carry none). */}
      {segments.some((s) => s.confidence !== null && s.confidence !== undefined) && (
        <>
          <label className="row" style={{ gap: 6, alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showOnlyCheck}
              onChange={(e) => setShowOnlyCheck(e.target.checked)}
            />
            {t('meeting.showOnlyCheck', { count: lowConfidenceCount })}
          </label>
          {lowConfidenceCount > 0 && (
            <p className="muted">
              {t('meeting.lowConfidenceSummary', { n: lowConfidenceCount, m: segments.length })}
            </p>
          )}
          <p className="muted" style={{ fontSize: '0.85rem' }}>
            {t('meeting.confidenceLegend')}
          </p>
        </>
      )}

      {segments.length === 0 && !processing && <p className="muted">{t('meeting.noTranscript')}</p>}
    </>
  );

  const minutesPanel = (
    <MomPanel
      meetingId={id}
      canWrite={canWrite}
      meetingStatus={meeting.status}
      onCite={jumpToSegmentIdx}
      onConfirmPeople={() => setPeopleDialog(true)}
      onMomLoaded={setHasMom}
    />
  );

  return (
    <>
      <div className="spread">
        <h1 style={{ marginBottom: 0 }}>{meeting.title}</h1>
        <StatusChip status={meeting.status} />
      </div>
      <p className="muted">
        {t(`types.${meeting.type}`)}
        {meeting.department?.name ? ` · ${meeting.department.name}` : ''}
        {meeting.clientRef ? ` · ${meeting.clientRef}` : ''}
        {meeting.durationSec ? ` · ${formatTimestamp(meeting.durationSec)}` : ''}
      </p>

      {/* The call's own reference — a Zoom meeting number. It prints in the
          document's source line, which is how somebody finds the call again. */}
      <p className="muted small">
        {t('meeting.externalRef')}:{' '}
        {editingRef ? (
          <span className="row" style={{ display: 'inline-flex', gap: 6 }}>
            <input
              dir="ltr"
              value={refDraft}
              style={{ maxWidth: 200 }}
              placeholder="856 1936 9526"
              onChange={(e) => setRefDraft(e.target.value)}
            />
            <button
              type="button"
              className="link"
              disabled={saveExternalRef.isPending}
              onClick={() => saveExternalRef.mutate(refDraft)}
            >
              {t('common.save')}
            </button>
            <button type="button" className="link" onClick={() => setEditingRef(false)}>
              {t('common.cancel')}
            </button>
          </span>
        ) : (
          <>
            <span dir="ltr">{meeting.externalRef ?? t('common.none')}</span>
            {canWrite && (
              <button
                type="button"
                className="link"
                style={{ marginInlineStart: 8 }}
                onClick={() => {
                  setRefDraft(meeting.externalRef ?? '');
                  setEditingRef(true);
                }}
              >
                {t('meeting.edit')}
              </button>
            )}
          </>
        )}
      </p>

      {notice && <div className="banner">{notice}</div>}
      {processing && <div className="banner">{t('meeting.processing')}</div>}
      {(meeting.status === 'FAILED' || meeting.status === 'NO_SPEECH') && (
        <div className="banner error">
          <strong>
            {meeting.status === 'FAILED' ? t('meeting.failed') : t('statuses.NO_SPEECH')}
          </strong>
          <div className="muted">{meeting.failureDetail}</div>
        </div>
      )}
      {segments.length > 0 && !hasAudio && !processing && (
        <div className="banner">
          {t('meeting.importedNoAudio')}{' '}
          <Link to={`/meetings/${id}/upload`}>{t('meeting.importedNoAudioLink')}</Link>
        </div>
      )}

      <div className="tabs" role="tablist" aria-label={t('meeting.tabs') as string}>
        {tabs.map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={activeTab === name}
            className={activeTab === name ? 'tab active' : 'tab'}
            onClick={() => {
              setTab(name);
              if (name !== 'transcript') setCameFromCitation(false);
            }}
          >
            {t(`meeting.tab.${name}`)}
          </button>
        ))}
      </div>

      {activeTab === 'minutes' &&
        (twoPane ? (
          <div className="review-split">
            <div className="review-minutes">{minutesPanel}</div>
            <aside className="review-transcript" aria-label={t('meeting.transcript') as string}>
              <div className="review-transcript-head">
                <span className="muted small">{t('meeting.transcript')}</span>
                {canWrite && segments.length > 0 && (
                  <button type="button" className="link" onClick={() => setPeopleDialog(true)}>
                    {t('people.open')}
                  </button>
                )}
              </div>
              <TranscriptPane
                meetingId={id}
                segments={visibleSegments}
                canWrite={canWrite}
                activeSegmentId={activeSegment}
                onSeek={seek}
                contained
              />
            </aside>
          </div>
        ) : (
          minutesPanel
        ))}

      {activeTab === 'transcript' && (
        <>
          {transcriptControls}
          <TranscriptPane
            meetingId={id}
            segments={visibleSegments}
            canWrite={canWrite}
            activeSegmentId={activeSegment}
            onSeek={seek}
          />
          {showBackToMinutes({ activeTab, cameFromCitation }) && (
            <button
              type="button"
              className="gold back-to-minutes"
              onClick={() => {
                setTab('minutes');
                setCameFromCitation(false);
              }}
            >
              {t('meeting.backToMinutes')}
            </button>
          )}
        </>
      )}

      {activeTab === 'diagnostics' && isAdmin && (
        <>
          <details className="card" open>
            <summary>{t('meeting.diagnostics')}</summary>
            <ClientLogTable rows={clientLogQuery.data?.items ?? []} />
          </details>
          <details className="card">
            <summary>{t('meeting.chunks')}</summary>
            <ChunkTable rows={clientLogQuery.data?.chunks ?? []} />
          </details>
        </>
      )}

      <div className="audio-bar">
        {audioSrc ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <audio
            ref={audioRef}
            controls
            preload="metadata"
            src={audioSrc}
            onError={() => void refreshAudio()}
          />
        ) : (
          <p className="muted" style={{ margin: 0 }}>
            {t('meeting.audioMissing')}
          </p>
        )}
      </div>

      {peopleDialog && (
        <ConfirmPeopleDialog
          speakers={speakers}
          attendees={meeting.attendees}
          realClientName={meeting.realClientName}
          preparedByOverride={meeting.preparedByOverride}
          busy={confirmPeople.isPending}
          onCancel={() => setPeopleDialog(false)}
          onSave={(input) => confirmPeople.mutate(input)}
        />
      )}
    </>
  );
}

/** 512000 -> "500 KB"; good enough for a diagnostics column, not a general utility. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function ClientLogTable({ rows }: { rows: ClientLogRow[] }): JSX.Element {
  const { t } = useTranslation();
  if (rows.length === 0) return <p className="muted">{t('meeting.diagnosticsEmpty')}</p>;
  return (
    <table dir="ltr">
      <tbody>
        {rows.map((row) => {
          const build = row.detail && typeof row.detail.build === 'string' ? row.detail.build : '';
          return (
            <tr key={row.id}>
              <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                {new Date(row.createdAt).toLocaleTimeString()}
              </td>
              <td style={{ color: row.level === 'info' ? undefined : 'var(--danger)' }}>
                {row.level}
              </td>
              <td>{row.event}</td>
              <td className="muted" title={t('meeting.build') as string}>
                {build}
              </td>
              <td className="muted" style={{ wordBreak: 'break-all' }}>
                {row.detail ? JSON.stringify(row.detail) : ''}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** Compact per-piece size/attempt table — the forensics that were lost when
 * RecordingChunk rows used to be deleted right after assembly. */
function ChunkTable({ rows }: { rows: ChunkDiagnosticRow[] }): JSX.Element {
  const { t } = useTranslation();
  if (rows.length === 0) return <p className="muted">{t('meeting.chunksEmpty')}</p>;
  return (
    <table dir="ltr">
      <thead>
        <tr>
          <th>{t('meeting.chunkIndex')}</th>
          <th>{t('meeting.chunkAttempt')}</th>
          <th>{t('meeting.chunkSize')}</th>
          <th>{t('meeting.chunkUploaded')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={`${row.attempt}-${row.index}`}>
            <td>{row.index}</td>
            <td>{row.attempt}</td>
            <td className="muted">{formatBytes(row.sizeBytes)}</td>
            <td className="muted" style={{ whiteSpace: 'nowrap' }}>
              {new Date(row.uploadedAt).toLocaleTimeString()}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
